<template>
  <div class="chat-panel" :class="{ 'chat-panel--full-height': fullHeight }">
    <div v-if="workInProgress" class="chat-panel__watermark" aria-hidden="true">
      <span class="chat-panel__watermark-badge">In Progress</span>
    </div>

    <div class="chat-panel__body" :inert="workInProgress">
      <!-- Conversation tabs, plus the affordance that starts a new one. -->
      <div class="chat-panel__tabs">
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          class="chat-panel__tab"
          :class="{ 'chat-panel__tab--active': conversation.id === activeConversationId }"
          type="button"
          @click="activeConversationId = conversation.id"
        >
          <span class="chat-panel__tab-top">
            {{ conversation.label }}
            <span v-if="conversation.count" class="chat-panel__tab-count">{{ conversation.count }}</span>
            <span
              v-if="conversation.closable"
              class="chat-panel__tab-close"
              title="Close conversation"
              @click.stop="emit('conversation-close', conversation.id)"
            ><i class="pi pi-times" /></span>
          </span>
          <span class="chat-panel__tab-subtitle">{{ conversation.sub }}</span>
        </button>

        <Button
          class="chat-panel__new-conversation"
          icon="pi pi-user-plus"
          title="Message a specific attendee"
          @click="toggleRoster"
        />

        <Popover ref="rosterPopover">
          <div class="chat-panel__roster">
            <div class="chat-panel__roster-head">Message attendee</div>
            <div v-if="roster.length === 0" class="chat-panel__roster-empty">No attendees online.</div>
            <button
              v-for="attendee in roster"
              :key="attendee.badge"
              class="chat-panel__roster-row"
              type="button"
              @click="openDirectMessage(attendee)"
            >
              <span class="chat-panel__roster-badge">{{ attendee.badge }}</span>
              <span class="chat-panel__roster-name">{{ attendee.name }}</span>
              <i class="pi pi-comment chat-panel__roster-icon" />
            </button>
          </div>
        </Popover>
      </div>

      <!-- Stream -->
      <div class="chat-panel__stream">
        <div v-if="note" class="chat-panel__note">{{ note }}</div>
        <MessageBubble
          v-for="bubble in bubbles"
          :key="bubble.id"
          :message="bubble.message"
          :group-start="bubble.groupStart"
          show-author
        />
      </div>

      <!-- Composer -->
      <MessageComposer
        v-model="draft"
        :placeholder="composerPlaceholder"
        :can-send="canSend"
        :show-attach="false"
        @send="send"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import { MessageBubble, MessageComposer } from '@hds/auto-auction-com-components-shared';

// The lane chat: tabbed conversations, a message stream, and a composer.
//
// The stream and the compose row are the SHARED messaging components, the same
// two the mobile app draws its 1:1 threads with. What the lane needs beyond a
// 1:1 thread is carried by two props rather than a second set of components:
// `showAuthor` draws the speaker's name at the head of each run, and each
// message's `role` tints it — so a clerk can tell the auctioneer from a buyer
// at a glance, which is the whole reason this panel is not a phone chat.
//
// The compose row is drawn at the one size it has. It used to be asked for a
// `compact` one on the argument that touch sizing is a phone fact — but a
// pointer hits a 44px target at least as easily as a 34px one, so the row lost
// the prop and this panel pays about 22px of panel height for it, out of the
// stream above.
//
// It used to hold the conversation list, the direct-message target and the
// roster itself, deriving all three from the console's whole `data` record —
// so the panel decided who the tabs were and the application could not have
// told it otherwise. It renders what it is given now and reports what the clerk
// did; the two things it still owns are genuinely local, the draft in the box
// and whether the attendee list is showing.
//
// The attendee list is a PrimeVue `Popover` rather than a hand-rolled panel
// with a document `mousedown` listener, which is what it was — outside-click,
// Esc and positioning all come free and correct.
//
// `workInProgress` blurs the interior behind an "In Progress" watermark and
// makes it `inert`. It defaults ON because the panel does not send anywhere
// yet, and a chat box that looks live during a sale is worse than one that
// admits it is not.
//
// SLOTS — none. Everything is data.
defineOptions({ name: 'ChatPanel' });

const props = defineProps({
  // [{ id, label, sub, count, closable }] — the tab strip, in order.
  conversations: { type: Array, default: () => [] },
  // [{ id, text, author, role, time, mine }] for the ACTIVE conversation,
  // oldest first.
  messages: { type: Array, default: () => [] },
  // [{ badge, name }] — who is in the sale and can be messaged directly.
  roster: { type: Array, default: () => [] },
  // A line above the stream, e.g. "Private conversation with …".
  note: { type: String, default: null },
  composerPlaceholder: { type: String, default: 'Message everyone in the lane…' },
  // Fill the band it sits in rather than sizing to its content.
  fullHeight: { type: Boolean, default: false },
  workInProgress: { type: Boolean, default: true },
});

const emit = defineEmits(['conversation-close', 'direct-message-open', 'send']);

const activeConversationId = defineModel({ type: [String, Number], default: null });

// The lane's messages in the shared bubble's shape, plus where a run from one
// author starts. Grouping needs the NEIGHBORING message, so it cannot be
// decided per-bubble — and it is presentation, not data, so it is not the
// application's to compute either.
//
// The mapping is this panel's job for the same reason: the runtime calls the
// speaker `author` and the viewer `mine`, and reconciling one feed's field
// names to a component's props is exactly what a consumer is for.
const bubbles = computed(() =>
  props.messages.map((message, index) => ({
    id: message.id,
    groupStart: index === 0 || props.messages[index - 1].author !== message.author,
    message: {
      kind: 'text',
      author: message.mine ? 'me' : 'them',
      authorName: message.author,
      role: message.role,
      body: message.text,
      timeLabel: message.time,
    },
  }))
);

const rosterPopover = ref(null);
const toggleRoster = (event) => rosterPopover.value?.toggle(event);

function openDirectMessage(attendee) {
  rosterPopover.value?.hide();
  emit('direct-message-open', attendee);
}

const draft = ref('');
const canSend = computed(() => draft.value.trim().length > 0);

function send() {
  const text = draft.value.trim();
  if (!text) return;
  emit('send', text);
  draft.value = '';
}
</script>

<style scoped>
.chat-panel {
  flex: 0.75 0.75 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: var(--p-chat-panel-background);
  border: var(--p-chat-panel-border-width) solid var(--p-chat-panel-border-color);
  border-radius: var(--p-chat-panel-border-radius);
}

.chat-panel--full-height {
  flex: 1;
  align-self: stretch;
  height: 100%;
  min-height: 0;
}

.chat-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chat-panel__body[inert] {
  filter: blur(0.75px);
  opacity: 0.65;
  user-select: none;
}

.chat-panel__watermark {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.chat-panel__watermark-badge {
  text-transform: uppercase;
  padding: var(--p-chat-panel-watermark-badge-padding);
  border-radius: var(--p-chat-panel-watermark-badge-border-radius);
  background: var(--p-chat-panel-watermark-badge-background);
  border: var(--p-chat-panel-watermark-badge-border-width) solid var(--p-chat-panel-watermark-badge-border-color);
  box-shadow: var(--p-chat-panel-watermark-badge-box-shadow);
  font-size: var(--p-chat-panel-watermark-badge-font-size);
  font-weight: var(--p-chat-panel-watermark-badge-font-weight);
  letter-spacing: var(--p-chat-panel-watermark-badge-letter-spacing);
  color: var(--p-chat-panel-watermark-badge-color);
}

/* ── Tabs ───────────────────────────────────────────────────────────────── */

.chat-panel__tabs {
  display: flex;
  align-items: stretch;
  position: relative;
  padding: var(--p-chat-panel-tabs-padding);
  border-bottom: var(--p-chat-panel-tabs-border-bottom-width) solid var(--p-chat-panel-tabs-border-bottom-color);
  gap: var(--p-chat-panel-tabs-gap);
}

.chat-panel__tab {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
  border: 0;
  gap: 1px;
  font-family: inherit;
  background: transparent;
  padding: var(--p-chat-panel-tab-padding);
  border-radius: var(--p-chat-panel-tab-border-radius);
  font-size: var(--p-chat-panel-tab-font-size);
  font-weight: var(--p-chat-panel-tab-font-weight);
  line-height: var(--p-chat-panel-tab-line-height);
  color: var(--p-chat-panel-tab-color);
}

.chat-panel__tab--active {
  background: var(--p-chat-panel-tab-active-background);
  color: var(--p-chat-panel-tab-active-color);
  font-weight: var(--p-chat-panel-tab-active-font-weight);
}

.chat-panel__tab-top {
  display: flex;
  align-items: center;
  gap: var(--p-chat-panel-tab-top-gap);
}

.chat-panel__tab-count {
  font-family: var(--p-chat-panel-tab-count-font-family);
  font-size: var(--p-chat-panel-tab-count-font-size);
  font-weight: var(--p-chat-panel-tab-count-font-weight);
  line-height: var(--p-chat-panel-tab-count-line-height);
  padding: var(--p-chat-panel-tab-count-padding);
  border-radius: var(--p-chat-panel-tab-count-border-radius);
  background: var(--p-chat-panel-tab-count-background);
  color: var(--p-chat-panel-tab-count-color);
}

.chat-panel__tab--active .chat-panel__tab-count {
  background: var(--p-chat-panel-tab-active-count-background);
  color: var(--p-chat-panel-tab-active-count-color);
}

.chat-panel__tab-close {
  margin-left: 2px;
  display: inline-flex;
  color: var(--p-chat-panel-tab-close-color);
  font-size: var(--p-chat-panel-tab-close-font-size);
}

.chat-panel__tab-subtitle {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-chat-panel-tab-subtitle-font-size);
  font-weight: var(--p-chat-panel-tab-subtitle-font-weight);
  color: var(--p-chat-panel-tab-subtitle-color);
}

/* Dashed, because it adds a conversation rather than switching to one. */
.p-button.chat-panel__new-conversation {
  width: 30px;
  align-self: stretch;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-style: dashed;
  border-width: var(--p-chat-panel-new-conversation-border-width);
  border-color: var(--p-chat-panel-new-conversation-border-color);
  border-radius: var(--p-chat-panel-new-conversation-border-radius);
  color: var(--p-chat-panel-new-conversation-color);
}

/* ── Attendee list ──────────────────────────────────────────────────────── */

.chat-panel__roster {
  width: 240px;
  max-height: 280px;
  overflow-y: auto;
}

.chat-panel__roster-head {
  text-transform: uppercase;
  padding: var(--p-chat-panel-roster-head-padding);
  font-size: var(--p-chat-panel-roster-head-font-size);
  font-weight: var(--p-chat-panel-roster-head-font-weight);
  letter-spacing: var(--p-chat-panel-roster-head-letter-spacing);
  color: var(--p-chat-panel-roster-head-color);
}

.chat-panel__roster-empty {
  padding: var(--p-chat-panel-roster-empty-padding);
  font-size: var(--p-chat-panel-roster-empty-font-size);
  color: var(--p-chat-panel-roster-empty-color);
}

.chat-panel__roster-row {
  width: 100%;
  display: flex;
  align-items: center;
  text-align: left;
  cursor: pointer;
  border: 0;
  background: transparent;
  font-family: inherit;
  gap: var(--p-chat-panel-roster-row-gap);
  padding: var(--p-chat-panel-roster-row-padding);
  border-radius: var(--p-chat-panel-roster-row-border-radius);
}

.chat-panel__roster-row:hover {
  background: var(--p-chat-panel-roster-row-hover-background);
}

.chat-panel__roster-badge {
  min-width: 44px;
  font-family: var(--p-chat-panel-roster-badge-font-family);
  font-size: var(--p-chat-panel-roster-badge-font-size);
  font-weight: var(--p-chat-panel-roster-badge-font-weight);
  color: var(--p-chat-panel-roster-badge-color);
}

.chat-panel__roster-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-chat-panel-roster-name-font-size);
  color: var(--p-chat-panel-roster-name-color);
}

.chat-panel__roster-icon {
  font-size: var(--p-chat-panel-roster-icon-font-size);
  color: var(--p-chat-panel-roster-icon-color);
}

/* ── Stream ─────────────────────────────────────────────────────────────── */

.chat-panel__stream {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: var(--p-chat-panel-stream-padding);
  gap: var(--p-chat-panel-stream-gap);
}

.chat-panel__note {
  text-align: center;
  padding: var(--p-chat-panel-note-padding);
  font-size: var(--p-chat-panel-note-font-size);
  color: var(--p-chat-panel-note-color);
}

/* The composer's own styles left with it. `MessageComposer` draws its border,
   padding, input and send button from its own token block, which is the point
   of sharing it — this panel no longer has an opinion about any of them. */
</style>
